import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import styles from "./Homepage.module.css";
import { Card, Heading, Logo, Text } from "../../ui/index.js";

/**
 * What a signed-out visitor lands on at `/` - see `AuthGate`, which is what
 * decides that rather than the router. Every other path still falls through
 * to the sign-in wall, `/login` included, so this is the only place that
 * needs a "log in" link at all.
 */
export function Homepage() {
  const [menuOpen, setMenuOpen] = useState(false);

  // Below 640px the nav is a dropdown rather than always on screen, so it
  // needs its own way to close - Escape, same as any other transient panel.
  useEffect(() => {
    if (!menuOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.brand}>
            <Logo className={styles.brandMark} />
            <span className={styles.brandName}>Squirrel</span>
          </div>

          <nav
            id="marketing-nav"
            className={styles.nav}
            aria-label="Page sections"
            data-open={menuOpen || undefined}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("a")) setMenuOpen(false);
            }}
          >
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
            <Link to="/security">Security</Link>
          </nav>

          <div className={styles.headerActions}>
            <Link to="/login" className={styles.loginLink}>
              Log in / Sign up
            </Link>
            <button
              type="button"
              className={styles.menuToggle}
              aria-expanded={menuOpen}
              aria-controls="marketing-nav"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((value) => !value)}
            >
              {menuOpen ? <IconClose /> : <IconMenu />}
            </button>
          </div>
        </div>
      </header>

      <main>
        <div className={styles.heroContainer}>
          <section className={styles.hero}>
            <div className={styles.heroText}>
              <Heading level={1} size="3xl">
                Household finances, in a nutshell.
              </Heading>
              <Text size="lg" tone="secondary" className={styles.heroSubtitle}>
                Import your statements, let Squirrel sort the transactions out, and finally see
                where the money goes - together with everyone else in the household.
              </Text>
              <div className={styles.heroActions}>
                <Link to="/login" className={styles.ctaPrimary}>
                  Get started for free
                </Link>
                <a href="#features" className={styles.ctaSecondary}>
                  See what it does
                </a>
              </div>
            </div>

            <div className={styles.heroArt} aria-hidden="true">
              <div className={styles.mockCardBack} />
              <div className={styles.mockCard}>
                <span className={styles.mockLabel}>This month</span>
                <ul className={styles.mockBars}>
                  <li className={styles.mockRow}>
                    <span>Groceries</span>
                    <span className={styles.mockTrack}>
                      <span className={`${styles.mockFill} ${styles.mockFill70}`} />
                    </span>
                  </li>
                  <li className={styles.mockRow}>
                    <span>Rent</span>
                    <span className={styles.mockTrack}>
                      <span className={`${styles.mockFill} ${styles.mockFill90}`} />
                    </span>
                  </li>
                  <li className={styles.mockRow}>
                    <span>Transport</span>
                    <span className={styles.mockTrack}>
                      <span className={`${styles.mockFill} ${styles.mockFill35}`} />
                    </span>
                  </li>
                  <li className={styles.mockRow}>
                    <span>Eating out</span>
                    <span className={styles.mockTrack}>
                      <span className={`${styles.mockFill} ${styles.mockFill55}`} />
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </div>

        <section id="features" className={styles.features}>
          <div className={styles.sectionIntro}>
            <Heading level={2} size="2xl">
              Everything you need to keep track of your finances.
            </Heading>
            <Text tone="secondary" size="lg">
              One place. No more juggling spreadsheets, banking apps, or shared notes nobody keeps
              updated.
            </Text>
          </div>

          <div className={styles.grid}>
            {FEATURES.map((feature) => (
              <Card as="article" key={feature.title} className={styles.featureCard}>
                <span className={styles.featureIcon}>{feature.icon}</span>
                <Heading level={3} size="lg">
                  {feature.title}
                </Heading>
                <Text tone="secondary">{feature.body}</Text>
              </Card>
            ))}
          </div>
        </section>

        <section id="how-it-works" className={styles.steps}>
          <Heading level={2} size="2xl" className={styles.stepsHeading}>
            How it works
          </Heading>

          <ol className={styles.stepList}>
            {STEPS.map((step, index) => (
              <li key={step.title} className={styles.step}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <Heading level={3} size="lg">
                  {step.title}
                </Heading>
                <Text tone="secondary">{step.body}</Text>
              </li>
            ))}
          </ol>
        </section>

        <section className={styles.ctaBanner}>
          <Heading level={2} size="2xl">
            Ready to see where it all goes?
          </Heading>
          <Text tone="secondary" size="lg">
            Free to start. Invite the rest of the household whenever you're ready.
          </Text>
          <Link to="/login" className={styles.ctaPrimary}>
            Get started for free
          </Link>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.brand}>
          <Logo className={styles.brandMark} />
          <span className={styles.brandName}>Squirrel</span>
        </div>
        <Text tone="muted" size="sm">
          Household finances in a nutshell.
        </Text>
        <Link to="/security" className={styles.footerLink}>
          How we look after your data
        </Link>
      </footer>
    </div>
  );
}

interface Feature {
  icon: ReactNode;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: <IconImport />,
    title: "Import statements",
    body: "Bring in CSV exports from any bank. Map the columns once and Squirrel remembers for next time.",
  },
  {
    icon: <IconRule />,
    title: "Rules that learn your spending",
    body: 'Teach it once - "anything from Tesco is Groceries" - and every future transaction sorts itself.',
  },
  {
    icon: <IconDuplicate />,
    title: "Duplicate detection",
    body: "Re-import the same statement by accident and Squirrel catches it before it doubles your totals.",
  },
  {
    icon: <IconTransfer />,
    title: "Transfer matching",
    body: "Moves between your own accounts are recognised automatically, so they never look like spending.",
  },
  {
    icon: <IconBudget />,
    title: "Budgets",
    body: "Set a monthly budget per category and watch progress build through the month, not just at the end.",
  },
  {
    icon: <IconInsights />,
    title: "Insights",
    body: "Trends and breakdowns over time, so a one-off big month doesn't get mistaken for a habit.",
  },
  {
    icon: <IconAsk />,
    title: "Ask, in plain English",
    body: '"How much did we spend on holidays this year?" Ask it directly and get an answer from your own ledger.',
  },
  {
    icon: <IconHousehold />,
    title: "Built for the household",
    body: "Invite your partner in. Shared accounts and categories, with roles so everyone sees what they should.",
  },
];

const STEPS = [
  {
    title: "Import your accounts",
    body: "Import statements for every account that matters - current, savings, credit card.",
  },
  {
    title: "Squirrel sorts it out",
    body: "Rules categorise transactions, transfers are matched, and duplicates are flagged automatically.",
  },
  {
    title: "See the full picture",
    body: "Budgets, insights, and 'Ask' are there whenever you want them.",
  },
];

/**
 * Inline SVG rather than an icon package: eight icons for a landing page is
 * not a dependency, and they inherit `currentColor` so the theme carries them.
 */
function IconImport() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M12 15V4m0 0L8 8m4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRule() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 6h16l-6 7v5l-4 2v-7L4 6Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconDuplicate() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15" />
    </svg>
  );
}

function IconTransfer() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 8h13m0 0-4-4m4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 16H7m0 0 4 4m-4-4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconBudget() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function IconInsights() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 15.5 9 10l3.5 3.5L20 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 6h5v5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconAsk() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path
        d="M20 12.5a7 7 0 0 1-7 7H8l-4 2.5V17a7 7 0 0 1 3-12.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 10.5a2.5 2.5 0 1 1 3 2.4V14" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 16.6h.01" strokeLinecap="round" />
    </svg>
  );
}

function IconHousehold() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="8.5" cy="8" r="3" />
      <path d="M2.5 20v-1a6 6 0 0 1 6-6" strokeLinecap="round" />
      <circle cx="17" cy="9.5" r="2.4" />
      <path d="M14.5 20v-1a5 5 0 0 1 7-4.6" strokeLinecap="round" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}
